import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import type { OpsEmployeeDirectoryItem, OpsRoleBundle } from "../../shared/operations-schemas";
import { Button } from "../components/ui/button";
import { SheetBody, SheetFooter, SheetForm } from "../components/ui/sheet";
import { FieldError } from "../components/ui/field";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { listCustomers, updateEmployeeCustomerRoles } from "./api";
import { EmployeeRoleOptions } from "./EmployeeRoleOptions";

function useCustomerRoleState(employee: OpsEmployeeDirectoryItem, open: boolean) {
  const queryClient = useQueryClient();
  const [query, setQuery] = useState("");
  const [submittedQuery, setSubmittedQuery] = useState("");
  const [organizationId, setOrganizationId] = useState("");
  const [selected, setSelected] = useState<string[]>([]);
  const customers = useQuery({
    queryKey: ["ops", "employee-customer-options", submittedQuery],
    queryFn: () =>
      listCustomers({
        ...(submittedQuery ? { query: submittedQuery } : {}),
        page: 1,
        pageSize: 50,
      }),
    enabled: open,
  });
  const mutation = useMutation({
    mutationFn: () => updateEmployeeCustomerRoles(employee.id, organizationId, selected),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["ops", "employees"] });
    },
  });

  function selectCustomer(nextOrganizationId: string) {
    setOrganizationId(nextOrganizationId);
    setSelected(
      employee.roleAssignments
        .filter((assignment) => assignment.organizationId === nextOrganizationId)
        .map((assignment) => assignment.roleKey),
    );
    mutation.reset();
  }

  return {
    customers,
    mutation,
    organizationId,
    query,
    selected,
    search: () => {
      setSubmittedQuery(query.trim());
      selectCustomer("");
    },
    selectCustomer,
    setQuery,
    setSelected,
  };
}

type CustomerRoleState = ReturnType<typeof useCustomerRoleState>;

function CustomerSelector({ employeeId, state }: { employeeId: string; state: CustomerRoleState }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={`customer-access-search-${employeeId}`}>Find customer</Label>
      <div className="flex gap-2">
        <Input
          id={`customer-access-search-${employeeId}`}
          maxLength={100}
          onChange={(event) => {
            state.setQuery(event.currentTarget.value);
          }}
          placeholder="Customer name"
          value={state.query}
        />
        <Button onClick={state.search} type="button" variant="outline">
          Search
        </Button>
      </div>
      <Label htmlFor={`customer-access-select-${employeeId}`}>Customer</Label>
      <select
        className="min-h-11 w-full rounded-md border border-input bg-card px-3 text-base outline-none focus-visible:border-primary focus-visible:shadow-[var(--field-focus-shadow)]"
        disabled={state.customers.isPending}
        id={`customer-access-select-${employeeId}`}
        onChange={(event) => {
          state.selectCustomer(event.currentTarget.value);
        }}
        value={state.organizationId}
      >
        <option value="">Select a customer</option>
        {(state.customers.data?.customers ?? []).map((customer) => (
          <option key={customer.id} value={customer.id}>
            {customer.name}
          </option>
        ))}
      </select>
    </div>
  );
}

/** Customer-scoped roles, as a view inside the employee's side sheet (ADR-0087). */
export function EmployeeCustomerAccessView({
  employee,
  roleBundles,
  onBack,
}: {
  employee: OpsEmployeeDirectoryItem;
  roleBundles: OpsRoleBundle[];
  onBack: () => void;
}) {
  const state = useCustomerRoleState(employee, true);
  const roles = roleBundles.filter((role) => role.status === "active");
  return (
    <SheetForm
      aria-label="Customer access"
      onSubmit={(event) => {
        event.preventDefault();
        if (state.organizationId) state.mutation.mutate(undefined, { onSuccess: onBack });
      }}
    >
      <SheetBody>
        <p className="text-muted-foreground">
          Choose one customer, then give only the roles this employee needs there. Saving with no
          roles removes their access to that customer.
        </p>
        <CustomerSelector employeeId={employee.id} state={state} />
        {state.organizationId ? (
          <EmployeeRoleOptions
            onChange={(roleKey, checked) => {
              state.setSelected((current) =>
                checked ? [...current, roleKey].sort() : current.filter((key) => key !== roleKey),
              );
            }}
            roles={roles}
            selected={state.selected}
          />
        ) : null}
        {state.customers.error ? <FieldError>{state.customers.error.message}</FieldError> : null}
        {state.mutation.error ? <FieldError>{state.mutation.error.message}</FieldError> : null}
      </SheetBody>
      <SheetFooter>
        <Button type="button" variant="ghost" onClick={onBack}>
          Cancel
        </Button>
        <Button disabled={!state.organizationId || state.mutation.isPending} type="submit">
          {state.mutation.isPending ? "Saving…" : "Save customer access"}
        </Button>
      </SheetFooter>
    </SheetForm>
  );
}
